import React, { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Loader2,
  Upload,
  X,
} from "lucide-react";
import { EventOperationsState } from "../../types";
import { parseCsvText } from "../../lib/crmImport";
import {
  EVENT_ATTENDEE_OPTIONAL_HEADERS,
  EVENT_ATTENDEE_REQUIRED_HEADERS,
  EVENT_ATTENDEE_TEMPLATE_HEADERS,
  EventAttendeeImportResult,
  parseEventAttendeeRows,
} from "../../lib/eventOperations";

interface EventAttendeeImportModalProps {
  open: boolean;
  state: EventOperationsState;
  onClose: () => void;
  onImport: (result: EventAttendeeImportResult) => void;
}

const csvCell = (value: string) => `"${value.replace(/"/g, '""')}"`;

export const EventAttendeeImportModal: React.FC<
  EventAttendeeImportModalProps
> = ({ open, state, onClose, onImport }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState("");
  const [processing, setProcessing] = useState(false);
  const [fileError, setFileError] = useState("");
  const [result, setResult] = useState<EventAttendeeImportResult | null>(null);

  useEffect(() => {
    if (!open) {
      setFileName("");
      setProcessing(false);
      setFileError("");
      setResult(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  }, [open]);

  if (!open) return null;

  const processFile = async (file?: File) => {
    if (!file) return;
    setFileName(file.name);
    setFileError("");
    setResult(null);
    if (file.size > 10 * 1024 * 1024) {
      setFileError("El archivo supera el máximo de 10 MB.");
      return;
    }
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (!["csv", "xlsx"].includes(extension || "")) {
      setFileError("Usa un archivo CSV o Excel .xlsx.");
      return;
    }
    setProcessing(true);
    try {
      const rows =
        extension === "csv"
          ? parseCsvText(await file.text())
          : await (await import("read-excel-file/browser")).readSheet(file);
      setResult(parseEventAttendeeRows(rows, state));
    } catch {
      setFileError(
        "No se pudo leer el archivo. Comprueba que no esté dañado o protegido.",
      );
    } finally {
      setProcessing(false);
    }
  };

  const downloadTemplate = () => {
    const sample = [
      "Ana",
      "Pérez",
      "ana@ejemplo.com",
      "Argentina",
      "+54 9 11 0000-0000",
      "Médica",
      "Hospital Ejemplo",
      "Cardiología",
      state.ticketTypes[0]?.name || "",
      "Pendiente",
      "Pago pendiente",
      "LinkedIn Ads",
      state.campaigns[0]?.name || "",
      "",
      "médicos;latam",
      "Importado desde plantilla",
      new Date().toISOString().slice(0, 10),
    ];
    const blob = new Blob(
      [
        `\uFEFF${EVENT_ATTENDEE_TEMPLATE_HEADERS.map(csvCell).join(",")}\n${sample.map(csvCell).join(",")}\n`,
      ],
      { type: "text/csv;charset=utf-8" },
    );
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "plantilla_inscriptos_grosym.csv";
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="fixed inset-0 z-[90] bg-slate-950/60 backdrop-blur-sm p-3 sm:p-6 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-3xl max-h-[92dvh] bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-emerald-500 text-slate-950 flex items-center justify-center">
              <FileSpreadsheet size={20} />
            </span>
            <div>
              <h2 className="font-display font-black text-slate-950">
                Importar inscriptos
              </h2>
              <p className="text-xs text-slate-500">
                CSV o Excel con validación antes de guardar.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-xl border border-slate-200 flex items-center justify-center"
            aria-label="Cerrar importador"
          >
            <X size={17} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <section className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-[10px] font-black uppercase text-emerald-900">
                  Columnas del archivo
                </p>
                <p className="text-[11px] text-emerald-800 mt-1">
                  La primera fila debe contener encabezados. Los tickets y
                  campañas pueden usar nombre o ID.
                </p>
              </div>
              <button
                onClick={downloadTemplate}
                className="h-9 px-3 rounded-xl bg-white border border-emerald-200 text-[10px] font-black text-emerald-800 flex items-center gap-2"
              >
                <Download size={13} /> Plantilla CSV
              </button>
            </div>
            <div className="mt-3">
              <p className="text-[9px] font-black uppercase text-slate-500">
                Obligatorios
              </p>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {EVENT_ATTENDEE_REQUIRED_HEADERS.map((header) => (
                  <span
                    key={header}
                    className="px-2 py-1 rounded-lg border border-rose-200 bg-rose-50 text-[10px] font-black text-rose-800"
                  >
                    {header} *
                  </span>
                ))}
              </div>
            </div>
            <div className="mt-3">
              <p className="text-[9px] font-black uppercase text-slate-500">
                Opcionales
              </p>
              <p className="text-[10px] text-slate-600 mt-1 leading-relaxed">
                {EVENT_ATTENDEE_OPTIONAL_HEADERS.join(" · ")}
              </p>
            </div>
          </section>
          <section className="rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 p-6 text-center">
            <input
              ref={inputRef}
              id="event-attendee-file"
              type="file"
              accept=".csv,.xlsx"
              className="sr-only"
              onChange={(event) => void processFile(event.target.files?.[0])}
            />
            <Upload size={25} className="mx-auto text-slate-400" />
            <p className="font-black text-sm mt-2">Selecciona el archivo</p>
            <p className="text-[11px] text-slate-500 mt-1">
              CSV o Excel .xlsx · máximo 10 MB
            </p>
            <label
              htmlFor="event-attendee-file"
              className="inline-flex mt-3 h-10 px-4 rounded-xl bg-slate-950 text-white items-center gap-2 text-xs font-black cursor-pointer"
            >
              <FileSpreadsheet size={14} /> Elegir archivo
            </label>
            {fileName && (
              <p className="text-xs font-bold text-slate-700 mt-2">
                {fileName}
              </p>
            )}
          </section>
          {processing && (
            <div className="rounded-xl border border-slate-200 p-4 flex items-center justify-center gap-2 text-xs font-bold">
              <Loader2 size={16} className="animate-spin text-emerald-600" />{" "}
              Analizando archivo…
            </div>
          )}
          {fileError && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 flex gap-2 text-xs font-bold text-rose-900">
              <AlertTriangle size={16} />
              {fileError}
            </div>
          )}
          {result?.missingRequiredHeaders.length ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-900">
              <p className="font-black">Faltan columnas obligatorias</p>
              <p className="mt-1">{result.missingRequiredHeaders.join(", ")}</p>
            </div>
          ) : null}
          {result && result.missingRequiredHeaders.length === 0 && (
            <section className="rounded-2xl border border-slate-200 overflow-hidden">
              <div className="p-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-2">
                <span className="text-xs font-black flex items-center gap-2">
                  <CheckCircle2 size={15} className="text-emerald-600" />{" "}
                  {result.attendees.length} válidos
                </span>
                {result.errors.length > 0 && (
                  <span className="text-[10px] font-bold text-amber-800">
                    {result.errors.length} filas omitidas
                  </span>
                )}
              </div>
              {result.attendees.length > 0 && (
                <div className="divide-y divide-slate-100">
                  {result.attendees.slice(0, 5).map((attendee) => (
                    <div
                      key={attendee.id}
                      className="px-4 py-3 grid grid-cols-[1fr_auto] gap-3"
                    >
                      <div>
                        <p className="text-xs font-black">
                          {attendee.firstName} {attendee.lastName}
                        </p>
                        <p className="text-[10px] text-slate-500">
                          {attendee.email} · {attendee.country}
                        </p>
                      </div>
                      <span className="text-[10px] font-bold text-slate-500">
                        {state.ticketTypes.find(
                          (ticket) => ticket.id === attendee.ticketTypeId,
                        )?.name || "Sin ticket"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {result.errors.length > 0 && (
                <div className="p-3 border-t border-amber-200 bg-amber-50">
                  <p className="text-[9px] font-black uppercase text-amber-900">
                    Errores
                  </p>
                  {result.errors.slice(0, 5).map((error) => (
                    <p
                      key={`${error.row}-${error.message}`}
                      className="text-[10px] text-amber-900 mt-1"
                    >
                      Fila {error.row}: {error.message}
                    </p>
                  ))}
                </div>
              )}
            </section>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="h-10 px-4 rounded-xl border border-slate-200 text-xs font-black"
          >
            Cancelar
          </button>
          <button
            disabled={!result?.attendees.length || processing}
            onClick={() => result && onImport(result)}
            className="h-10 px-4 rounded-xl bg-emerald-500 disabled:bg-slate-200 disabled:text-slate-400 text-slate-950 text-xs font-black"
          >
            Importar {result?.attendees.length || 0}
          </button>
        </div>
      </div>
    </div>
  );
};
